import {
  ShieldAlert,
  Ban,
  AlertTriangle,
  Mail,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Lock,
} from "lucide-react";

const FraudAlert = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Header */}
      <div className="bg-slate-900 text-white pt-10 pb-24">
        <div className="max-w-4xl mx-auto px-4 xl:px-6 sm:px-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8 font-semibold text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center border border-red-500/50">
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <div className="text-red-400 font-bold uppercase tracking-widest text-xs mb-1">
                Safety Advisory
              </div>
              <h1 className="text-4xl sm:text-3xl font-black tracking-tight">
                Fraud & Safety Alert
              </h1>
            </div>
          </div>

          <p className="text-xl text-slate-300 max-w-2xl leading-relaxed">
            We are committed to providing a safe job search experience. Please
            read this important information to protect yourself from scams.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 xl:px-6 sm:px-8 -mt-10 relative z-10 space-y-8 pb-20">
        {/* Main Alert Card */}
        <div className="bg-white rounded-3xl p-10 sm:p-8 shadow-xl border-t-4 border-red-500">
          <h2 className="text-2xl font-black text-slate-900 mb-6 flex items-center gap-3">
            <Ban className="w-8 h-8 text-red-600" />
            Freshment NEVER Charges Students
          </h2>

          <div className="prose prose-slate max-w-none">
            <p className="text-lg font-medium text-slate-700 leading-relaxed mb-6">
              <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded font-bold">
                IMPORTANT:
              </span>{" "}
              Freshment is a free platform for job seekers. We{" "}
              <span className="underline decoration-red-500 decoration-4 underline-offset-2">
                do not
              </span>{" "}
              charge any money for:
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-1 gap-4 mb-8">
              {[
                "Registration / Sign Up",
                "Applying to Jobs",
                "Interview Scheduling",
                "Offer Letters",
                "Training / Equipment Fees",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100"
                >
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="font-bold text-slate-700">
                    {item} is FREE
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-red-50 border border-red-100 rounded-2xl p-6 flex flex-row sm:flex-col gap-6 items-center text-left sm:text-center">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-1">
                  If anyone asks you for money...
                </h3>
                <p className="text-red-700 font-medium">
                  It is likely a scam. Do not pay. Report it to us immediately.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Warning Signs Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-6">
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <XCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">
                Consultancy Warning
              </h3>
            </div>
            <p className="text-slate-600 leading-relaxed font-medium">
              We <strong>do not encourage</strong> jobs via consultancies that
              ask for a "security deposit" or "half-month salary" in advance.
              Always verify if you are speaking to a direct company HR or a
              third-party agent.
            </p>
          </div>

          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">
                Official Communication
              </h3>
            </div>
            <p className="text-slate-600 leading-relaxed font-medium">
              Legitimate emails from Freshment will always come from an{" "}
              <strong>@freshmentapp.com</strong> domain. Be wary of emails from
              generic domains (gmail, yahoo) claiming to represent us.
            </p>
          </div>
        </div>

        {/* Report Section */}
        <div className="bg-slate-900 text-white rounded-3xl p-12 sm:p-8 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-[100px] opacity-20 -mr-20 -mt-20"></div>

          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">
              Report a Suspicious Activity
            </h2>
            <p className="text-slate-300 mb-8 leading-relaxed font-medium">
              If you encounter a job posting that asks for payment, or if you
              receive a suspicious email claiming to be from Freshment, please
              let us know immediately.
            </p>

            <a
              href="mailto:contact@freshmentapp.com"
              className="inline-flex items-center flex-wrap break-all justify-center xsm:px-4 gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-red-600/30 hover:scale-105"
            >
              <Mail className="w-5 h-5" />
              Email: contact@freshmentapp.com
            </a>
            <p className="mt-6 text-xs text-slate-500 uppercase tracking-widest font-bold">
              Your identity will be kept confidential
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FraudAlert;
